import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
    FileText, Plus, Clock, CheckCircle2, XCircle, Package,
    Search, ArrowLeft, Send, Loader2, ShoppingCart,
    ClipboardList, PlayCircle, AlertCircle,
    X, Pencil, Check, ChevronDown, PackagePlus, History, Power, FileSpreadsheet, Info, StopCircle, UserCircle, Printer, Download, BarChart2, AlertTriangle, PackageSearch, Save
} from 'lucide-react';
import { PieChart, Pie, Cell, BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import toast from 'react-hot-toast';
import TableView from './TableView';

// --- Constantes ---------------------------------------------------------------
// Una sola firma visual para el catálogo: azul corporativo, sin asignar un
// color diferente a cada categoría.
const CATEGORIA_BADGE_CLASS = 'bg-blue-50 text-sura-blue border-blue-100';

const ESTADO_CONFIG = {
    pendiente:   { label: 'Pendiente',   icon: Clock,        cls: 'bg-amber-50 text-amber-600 border-amber-100' },
    en_gestion:  { label: 'En gestión',  icon: PlayCircle,   cls: 'bg-blue-50 text-blue-600 border-blue-100' },
    completada:  { label: 'Entregada',   icon: CheckCircle2, cls: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
    recibido:    { label: 'Recibida',    icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
    rechazada:   { label: 'Rechazada',   icon: XCircle,      cls: 'bg-red-50 text-red-600 border-red-100' },
};

// --- Sub-componentes ----------------------------------------------------------
const EstadoBadge = ({ estado, parcial }) => {
    const base = ESTADO_CONFIG[estado] || ESTADO_CONFIG.pendiente;
    const c = parcial
        ? { label: 'Pendiente por entregar lo faltante', icon: Clock, cls: 'bg-orange-50 text-orange-600 border-orange-200' }
        : base;
    const Icon = c.icon;
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium uppercase tracking-wider border ${c.cls}`}>
            <Icon size={12} /> {c.label}
        </span>
    );
};

const StatCard = ({ title, value, icon: Icon, color, onClick, active }) => (
    <div onClick={onClick} className={`bg-white p-6 rounded-3xl border ${active ? 'border-sura-blue shadow-md ring-2 ring-sura-blue/10' : 'border-gray-100 shadow-sm'} ${onClick ? 'cursor-pointer hover:shadow-md transition-all' : ''} flex items-center gap-4`}>
        <div className={`p-4 rounded-2xl ${color}`}>
            <Icon size={24} className="text-white" />
        </div>
        <div>
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        </div>
    </div>
);

// Plural sencillo para nombres de empaque: Caja -> Cajas, Unidad -> Unidades
const pluralEmpaque = (nombre) => {
    const t = String(nombre || '').trim();
    if (!t || /s$/i.test(t)) return t;
    return /[aeiouáéíóú]$/i.test(t) ? `${t}s` : `${t}es`;
};

// Formas de ingresar stock de un articulo: sus empaques (Caja de 12, ...) y la unidad base.
// El primero es el empaque principal, como antes; la unidad base va al final.
const formatosIngreso = (art) => {
    const base = { nombre: art.unidad || 'Unidad', equivalencia: 1 };
    const empaques = (art.unidades_adicionales || [])
        .filter(u => u && String(u.nombre || '').trim() && Number(u.equivalencia) > 0)
        .map(u => ({ nombre: String(u.nombre).trim(), equivalencia: Number(u.equivalencia) }));
    const yaEstaLaBase = empaques.some(f => f.equivalencia === 1 && f.nombre.toLowerCase() === base.nombre.toLowerCase());
    return yaEstaLaBase ? empaques : [...empaques, base];
};

// Selector del formato de ingreso. Lista propia (no el <select> del navegador)
// para que siempre se despliegue hacia abajo, aunque la tarjeta este al final.
const SelectorFormato = ({ formatos, value, onChange, unidadBase, label }) => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const fuera = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        const escape = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', fuera);
        document.addEventListener('keydown', escape);
        return () => {
            document.removeEventListener('mousedown', fuera);
            document.removeEventListener('keydown', escape);
        };
    }, [open]);

    const texto = (f) => `${pluralEmpaque(f.nombre)}${f.equivalencia > 1 ? ` · 1 = ${f.equivalencia} ${pluralEmpaque(unidadBase)}` : ''}`;

    return (
        <div ref={ref} className="relative">
            <button type="button" onClick={() => setOpen(o => !o)} aria-haspopup="listbox" aria-expanded={open} aria-label={label}
                className={`h-12 w-full flex items-center justify-between gap-2 px-4 border rounded-xl bg-white text-sm font-medium text-gray-700 transition-colors ${open ? 'border-sura-blue ring-2 ring-sura-blue/10' : 'border-gray-200 hover:border-gray-300'}`}>
                <span className="truncate">{texto(formatos[value] || formatos[0])}</span>
                <ChevronDown size={16} className={`shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <ul role="listbox" className="absolute left-0 right-0 top-full mt-1.5 z-30 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg p-1">
                    {formatos.map((f, i) => (
                        <li key={`${f.nombre}-${i}`}>
                            <button type="button" role="option" aria-selected={i === value}
                                onClick={() => { onChange(i); setOpen(false); }}
                                className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${i === value ? 'bg-blue-50 text-sura-blue font-semibold' : 'text-gray-700 hover:bg-gray-50'}`}>
                                <span>{texto(f)}</span>
                                {i === value && <Check size={14} />}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

// --- Componente principal -----------------------------------------------------
const SolicitudPapeleriaView = () => {
    const { user } = useAuth();
    // El backend decide quien gestiona (rol procura, area de Procura u
    // observador especial) y lo manda en el usuario. El fallback cubre sesiones
    // viejas cuyo token se guardo antes de que existiera el campo.
    const esProcura = (user?.ve_gestion_procura ?? (user?.role === 'procura')) || user?.role === 'admin';
    const esRecepcionCA1 = user?.role === 'recepcion' && user?.sede_codigo === 'CA1';
    
    // Alias para simplificar condiciones
    const puedeVerEstadisticas = esProcura;
    const puedeVerReportes = esProcura;
    const puedeVerBandeja = esProcura || esRecepcionCA1;
    const puedeVerCatalogo = esProcura || esRecepcionCA1;
    const puedeVerReponerStock = esProcura;
    
    const [searchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    // Vistas: list | create | detail | catalogo | bandeja
    // Tabs permitidos por rol (evita que un empleado fuerce ?tab=catalogo o ?tab=bandeja)
    const tabsPermitidos = useMemo(() => {
        const tabs = ['list', 'create', 'detail'];
        if (puedeVerBandeja) tabs.push('bandeja');
        if (puedeVerCatalogo) tabs.push('catalogo');
        if (puedeVerReponerStock) tabs.push('reponer_stock');
        if (puedeVerEstadisticas) tabs.push('estadisticas');
        if (puedeVerReportes) tabs.push('reportes');
        return tabs;
    }, [puedeVerBandeja, puedeVerCatalogo, puedeVerReponerStock, puedeVerEstadisticas, puedeVerReportes]);
    const tabInicial = (tabParam && tabsPermitidos.includes(tabParam))
        ? tabParam
        : (puedeVerEstadisticas ? 'estadisticas' : (puedeVerBandeja ? 'bandeja' : 'list'));
    const [viewMode, setViewMode] = useState(tabInicial);

    // Sincronizar parámetro 'tab' con 'viewMode' solo si es un tab permitido para este rol
    useEffect(() => {
        if (tabParam && tabsPermitidos.includes(tabParam)) {
            setViewMode(tabParam);
        }
    }, [tabParam, tabsPermitidos]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [errorCarga, setErrorCarga] = useState(null);

    // Datos
    const [articulos, setArticulos] = useState([]);
    const [solicitudes, setSolicitudes] = useState([]);
    const [sede, setSede] = useState(null);
    const [selectedSol, setSelectedSol] = useState(null);

    // Formulario solicitud
    const [busqueda, setBusqueda] = useState('');
    const [categoriaActiva, setCategoriaActiva] = useState('Todas');
    const [carrito, setCarrito] = useState({});  // { "artId_formato": { articulo_id, cantidad, formato } }
    const [formatoActivo, setFormatoActivo] = useState({}); // { artId: formato_elegido }
    const [activeActionMenu, setActiveActionMenu] = useState(null);

    // Recepción: completar
    const [showCompletarModal, setShowCompletarModal] = useState(false);
    const [entregas, setEntregas] = useState({});
    // Lo que falte ya no se va a entregar: la solicitud se cierra con lo entregado.
    const [cerrarConLoEntregado, setCerrarConLoEntregado] = useState(false);
    const [notaRecepcion, setNotaRecepcion] = useState('');

    // Recepción: rechazar
    const [showRechazarModal, setShowRechazarModal] = useState(false);
    const [motivoRechazo, setMotivoRechazo] = useState('');

    // Gestión catálogo
    const [showCatalogoForm, setShowCatalogoForm] = useState(false);
    const [editandoArticulo, setEditandoArticulo] = useState(null);
    const [formArticulo, setFormArticulo] = useState({ nombre: '', categoria: 'ARTÍCULOS DE OFICINA', unidad: 'Unidad', stock_cdn: 0, stock_ca1: 0, stock_minimo: 5, limite_solicitud: '', activo: true, unidades_adicionales: [] });
    // Catálogo de gestión (incluye inactivos), kardex y reposición
    const [articulosCatalogo, setArticulosCatalogo] = useState([]);
    const [showKardexModal, setShowKardexModal] = useState(false);
    const [articuloKardex, setArticuloKardex] = useState(null);
    const [movimientos, setMovimientos] = useState([]);
    const [loadingMov, setLoadingMov] = useState(false);
    const [columnVisibility, setColumnVisibility] = useState({});

    // Estadísticas y Reportes
    const [statsData, setStatsData] = useState(null);
    const [loadingStats, setLoadingStats] = useState(false);
    const [rangoStats, setRangoStats] = useState('todo');
    const [filtroBandeja, setFiltroBandeja] = useState('pendiente');
    
    // Estado Reponer Stock
    const [reponerSearch, setReponerSearch] = useState('');
    const [reponerCategoria, setReponerCategoria] = useState('Todas');
    const [reponerInputs, setReponerInputs] = useState({}); // { id: { ca1: 0, cdn: 0 } }
    
    const [filtrosReporte, setFiltrosReporte] = useState({ estado: '', sede_id: '' });

    // Formato con el que se ingresa cada articulo (indice en formatosIngreso)
    const [reponerFormato, setReponerFormato] = useState({});

    const updateReponerInput = (id, sede, change) => {
        setReponerInputs(prev => {
            const current = prev[id] || { ca1: 0, cdn: 0 };
            return { ...prev, [id]: { ...current, [sede]: Math.max(0, current[sede] + change) } };
        });
    };

    const handleGuardarIngresoStock = async (art, formato) => {
        // Una cantidad que no se toco cuenta como 0 (escribir solo en CDN dejaba CA1 vacio
        // y el servidor recibia null: "int() argument must be ... not 'NoneType'")
        const ca1 = Number(reponerInputs[art.id]?.ca1) || 0;
        const cdn = Number(reponerInputs[art.id]?.cdn) || 0;
        if (ca1 <= 0 && cdn <= 0) {
            toast.error('Debe añadir una cantidad mayor a 0 para ingresar stock.');
            return;
        }

        const { nombre, equivalencia } = formato || formatosIngreso(art)[0];
        const total = cdn + ca1;

        try {
            setSubmitting(true);
            const payload = {
                motivo_ajuste: `Entrada de mercancía (${total} ${total === 1 ? nombre : pluralEmpaque(nombre)})`,
                stock_ca1: (Number(art.stock_ca1) || 0) + ca1 * equivalencia,
                stock_cdn: (Number(art.stock_cdn) || 0) + cdn * equivalencia,
            };
            await axiosInstance.put(`/api/papeleria/articulos/${art.id}`, payload);
            toast.success(`Stock ingresado correctamente a ${art.nombre}`);
            setReponerInputs(prev => ({ ...prev, [art.id]: { ca1: 0, cdn: 0 } }));
            await Promise.all([fetchArticulos(), fetchArticulosCatalogo()]);
        } catch (error) {
            toast.error(error.response?.data?.error || 'Error al ingresar stock');
        } finally {
            setSubmitting(false);
        }
    };

    // --- BLOQUEO DE SCROLL PARA MODALES ---------------------------------------
    useEffect(() => {
        const mainEl = document.querySelector('main');
        if (showCatalogoForm || showCompletarModal || showRechazarModal || showKardexModal) {
            document.body.style.overflow = 'hidden';
            if (mainEl) mainEl.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
            if (mainEl) mainEl.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
            if (mainEl) mainEl.style.overflow = '';
        };
    }, [showCatalogoForm, showCompletarModal, showRechazarModal, showKardexModal]);

    // -- Fetch ------------------------------------------------------------------
    const fetchArticulos = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/papeleria/articulos');
            setArticulos(res.data.articulos || []);
            setSede(res.data.sede || null);
        } catch (err) {
            setErrorCarga(err.response?.data?.error || 'No se pudo cargar el catálogo de papelería.');
        }
    }, []);

    const fetchSolicitudes = useCallback(async () => {
        try {
            setLoading(true);
            const res = await axiosInstance.get('/api/papeleria/solicitudes');
            setSolicitudes(res.data.solicitudes || []);
        } catch (err) {
            setErrorCarga(err.response?.data?.error || 'No se pudieron cargar las solicitudes de papelería.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Catálogo de gestión: incluye inactivos (?todos=1) para poder administrarlos
    const fetchArticulosCatalogo = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/api/papeleria/articulos?todos=1');
            setArticulosCatalogo(res.data.articulos || []);
        } catch (err) {
            setErrorCarga(err.response?.data?.error || 'No se pudo cargar el catálogo de gestión.');
        }
    }, []);

    const fetchEstadisticas = useCallback(async (rango = 'todo') => {
        try {
            setLoadingStats(true);
            const res = await axiosInstance.get(`/api/papeleria/estadisticas?rango=${rango}`);
            setStatsData(res.data);
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al cargar estadísticas');
        } finally {
            setLoadingStats(false);
        }
    }, []);

    useEffect(() => {
        if (viewMode === 'catalogo' || viewMode === 'reponer_stock') fetchArticulosCatalogo();
        if (viewMode === 'estadisticas') fetchEstadisticas(rangoStats);
    }, [viewMode, rangoStats, fetchArticulosCatalogo, fetchEstadisticas]);

    useEffect(() => {
        setErrorCarga(null);
        fetchSolicitudes();
        fetchArticulos();
    }, [fetchSolicitudes, fetchArticulos]);

    // -- Catálogo filtrado ------------------------------------------------------
    const categorias = ['Todas', ...Array.from(new Set(articulos.map(a => a.categoria)))];
    const articulosFiltrados = articulos.filter(a => {
        const matchCat = categoriaActiva === 'Todas' || a.categoria === categoriaActiva;
        const matchBusq = a.nombre.toLowerCase().includes(busqueda.toLowerCase());
        return matchCat && matchBusq;
    });

    const totalCarrito = Object.values(carrito).reduce((s, item) => s + item.cantidad, 0);
    const articulosEnCarrito = Object.values(carrito).map(item => {
        const art = articulos.find(a => a.id === item.articulo_id);
        return { ...art, cantidad: item.cantidad, formatoElegido: item.formato, cartKey: `${item.articulo_id}_${item.formato}` };
    });

    // -- Carrito ----------------------------------------------------------------
    const getStockDisponible = (artId) => {
        const art = articulos.find(a => a.id === parseInt(artId, 10));
        return art ? art.stock_disponible : null;
    };

    const getFormatosArticulo = (art) => {
        // Siempre "Unidad" como primera opción para el empleado
        const fmts = ['Unidad'];
        if (art.unidades_adicionales && art.unidades_adicionales.length > 0) {
            const empaque = art.unidades_adicionales[0];
            // Agregar el empaque si es distinto a "Unidad" (ej: Caja, Paquete, Bulto)
            if (empaque.nombre && empaque.nombre !== 'Unidad') {
                fmts.push(empaque.nombre);
            }
        } else if (art.unidad && art.unidad !== 'Unidad') {
            // Fallback: si no hay empaque definido, usar la unidad base
            fmts.push(art.unidad);
        }
        return fmts;
    };

    const setCantidad = (artId, formato, delta) => {
        const key = `${artId}_${formato}`;
        setCarrito(prev => {
            const actual = prev[key] ? prev[key].cantidad : 0;
            const deltaEquivalencia = formato === 'Unidad' ? delta : delta * (articulos.find(a => a.id === artId)?.unidades_adicionales?.find(u => u.nombre === formato)?.equivalencia || 1);
            let nueva = Math.max(0, actual + deltaEquivalencia);
            
            const art = articulos.find(a => a.id === artId);
            if (art?.limite_solicitud && nueva > art.limite_solicitud) {
                toast.error(`El límite máximo por solicitud para este artículo es de ${art.limite_solicitud} unidades.`);
                nueva = actual; // Revertir aumento
            }
            
            if (nueva === 0) {
                const { [key]: _, ...resto } = prev;
                return resto;
            }
            return { ...prev, [key]: { articulo_id: artId, cantidad: nueva, formato } };
        });
    };

    const setCantidadDirecta = (artId, formato, val) => {
        const key = `${artId}_${formato}`;
        let n = parseInt(val, 10);
        if (isNaN(n) || n <= 0) {
            const { [key]: _, ...resto } = carrito;
            setCarrito(resto);
        } else {
            const art = articulos.find(a => a.id === artId);
            if (art?.limite_solicitud && n > art.limite_solicitud) {
                toast.error(`El límite máximo por solicitud para este artículo es de ${art.limite_solicitud} unidades.`);
                n = art.limite_solicitud;
            }
            setCarrito(prev => ({ ...prev, [key]: { articulo_id: artId, cantidad: n, formato } }));
        }
    };

    // -- Enviar solicitud -------------------------------------------------------
    const handleEnviar = async () => {
        if (Object.keys(carrito).length === 0) return;
        setSubmitting(true);
        try {
            const items = Object.values(carrito).map(item => ({
                articulo_id: item.articulo_id,
                cantidad: item.cantidad,
                formato: item.formato
            }));
            await axiosInstance.post('/api/papeleria/solicitudes', { items });
            setCarrito({});
            setBusqueda('');
            setCategoriaActiva('Todas');
            await fetchSolicitudes();
            setViewMode('list');
            toast.success('Solicitud enviada');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al enviar solicitud');
        } finally {
            setSubmitting(false);
        }
    };

    // -- Completar (recepción) --------------------------------------------------
    const abrirCompletar = (sol) => {
        const init = {};
        sol.items.forEach(i => { init[i.id] = i.cantidad_pendiente ?? i.cantidad_solicitada; });
        setEntregas(init);
        setCerrarConLoEntregado(false);
        setNotaRecepcion('');
        setSelectedSol(sol);
        setShowCompletarModal(true);
    };

    const handleCompletar = async () => {
        setSubmitting(true);
        try {
            const res = await axiosInstance.post(`/api/papeleria/solicitudes/${selectedSol.id}/completar`, {
                entregas,
                nota_recepcion: notaRecepcion,
                cerrar: cerrarConLoEntregado,
            });
            setShowCompletarModal(false);
            await fetchSolicitudes();
            await fetchArticulos();
            setViewMode('bandeja');
            toast.success(res.data?.estado === 'pendiente'
                ? 'Entrega parcial registrada: la solicitud sigue pendiente por lo faltante'
                : cerrarConLoEntregado ? 'Solicitud cerrada con lo entregado' : 'Entrega registrada');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error');
        } finally {
            setSubmitting(false);
        }
    };

    // -- Rechazar (recepción) ---------------------------------------------------
    const handleRechazar = async () => {
        if (!motivoRechazo.trim()) return;
        setSubmitting(true);
        try {
            await axiosInstance.post(`/api/papeleria/solicitudes/${selectedSol.id}/rechazar`, {
                motivo_rechazo: motivoRechazo,
            });
            setShowRechazarModal(false);
            setMotivoRechazo('');
            await fetchSolicitudes();
            setViewMode('bandeja');
            toast.success('Solicitud rechazada');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error');
        } finally {
            setSubmitting(false);
        }
    };

    // -- Confirmar recepción (solicitante) --------------------------------------
    const handleConfirmarRecepcion = async (id) => {
        setSubmitting(true);
        try {
            await axiosInstance.post(`/api/papeleria/solicitudes/${id}/confirmar-recepcion`);
            await fetchSolicitudes();
            setViewMode('list');
            toast.success('Recepción confirmada');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error al confirmar la recepción');
        } finally {
            setSubmitting(false);
        }
    };

    // -- Gestión catálogo -------------------------------------------------------
    const handleGuardarArticulo = async () => {
        if (!formArticulo.nombre.trim()) return;
        setSubmitting(true);
        try {
            if (editandoArticulo) {
                await axiosInstance.put(`/api/papeleria/articulos/${editandoArticulo.id}`, formArticulo);
            } else {
                await axiosInstance.post('/api/papeleria/articulos', formArticulo);
            }
            setShowCatalogoForm(false);
            setEditandoArticulo(null);
            setFormArticulo({ nombre: '', categoria: 'ARTÍCULOS DE OFICINA', unidad: 'Unidad', stock_cdn: 0, stock_ca1: 0, stock_minimo: 5, limite_solicitud: '', activo: true, unidades_adicionales: [] });
            await Promise.all([fetchArticulos(), fetchArticulosCatalogo()]);
            toast.success('Artículo guardado');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error');
        } finally {
            setSubmitting(false);
        }
    };

    // -- Kardex (historial de movimientos) --------------------------------------
    const abrirKardex = async (art) => {
        setArticuloKardex(art);
        setMovimientos([]);
        setShowKardexModal(true);
        setLoadingMov(true);
        try {
            const res = await axiosInstance.get(`/api/papeleria/articulos/${art.id}/movimientos`);
            setMovimientos(res.data.movimientos || []);
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo cargar el kardex');
        } finally {
            setLoadingMov(false);
        }
    };

    // -- Activar/desactivar artículo --------------------------------------------
    const handleToggleActivo = async (art) => {
        try {
            await axiosInstance.put(`/api/papeleria/articulos/${art.id}`, { activo: !art.activo });
            await Promise.all([fetchArticulos(), fetchArticulosCatalogo()]);
            toast.success(art.activo ? 'Artículo desactivado' : 'Artículo activado');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Error');
        }
    };

    // --- THEMES POR CATEGORÍA --------------------------------------------------
    const getCategoriaTheme = (cat) => {
        return {
            base: 'sura-blue', text: 'text-sura-blue', bg: 'bg-sura-blue', bgLight: 'bg-sura-blue/5',
            borderActive: 'border-sura-blue', borderLight: 'border-sura-blue/30', dot: 'bg-sura-blue', headerText: 'text-sura-blue-dark'
        };
    };

    // --- RENDER: Vista de catálogo para solicitar ------------------------------
    const renderCrear = () => (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
            <div className="flex items-center gap-3">
                <button onClick={() => setViewMode('list')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h2 className="text-lg font-bold text-gray-900">Nueva solicitud de papelería</h2>
                    {sede && <p className="text-xs text-gray-500 mt-0.5">Sede: <span className="font-medium text-sura-blue">{sede}</span></p>}
                </div>
            </div>

            {/* Buscador + filtro categoría */}
            <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative flex-1">
                    <Search size={15} className="absolute left-3 top-3 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Buscar artículo..."
                        value={busqueda}
                        onChange={e => setBusqueda(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/20 focus:border-sura-blue/40"
                    />
                </div>
                <div className="flex gap-1 flex-wrap">
                    {categorias.map(cat => {
                        const theme = getCategoriaTheme(cat);
                        const isAll = cat === 'Todas';
                        const isActive = categoriaActiva === cat;
                        
                        let baseClasses = 'px-3 py-2 rounded-xl text-xs font-medium transition-colors border ';
                        if (isActive) {
                            baseClasses += isAll ? 'bg-gray-800 text-white border-gray-800' : `${theme.bg} text-white ${theme.borderActive}`;
                        } else {
                            baseClasses += `bg-white text-gray-600 border-gray-200 hover:border-gray-300 hover:bg-gray-50`;
                        }
                        
                        return (
                            <button key={cat} onClick={() => setCategoriaActiva(cat)} className={baseClasses}>
                                {cat}
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Layout principal: Lista a la izquierda, Carrito a la derecha */}
            <div className="flex flex-col lg:flex-row gap-6 items-start relative">
                
                {/* Contenedor de la lista de artículos */}
                <div className="flex-1 space-y-8 min-w-0 pb-40">
                    {(() => {
                        const agrupados = articulosFiltrados.reduce((acc, art) => {
                        if (!acc[art.categoria]) acc[art.categoria] = [];
                        acc[art.categoria].push(art);
                        return acc;
                    }, {});

                    if (articulosFiltrados.length === 0) {
                        return (
                            <div className="text-center py-12 text-gray-400">
                                <Package size={40} className="mx-auto mb-3 opacity-30" />
                                <p className="text-sm">No se encontraron artículos</p>
                            </div>
                        );
                    }

                    return Object.keys(agrupados).sort().map(cat => {
                        const theme = getCategoriaTheme(cat);
                        return (
                        <div key={cat}>
                            <h3 className={`text-sm font-bold ${theme.headerText} mb-3 uppercase tracking-wider flex items-center gap-2`}>
                                <div className={`w-1.5 h-1.5 rounded-full ${theme.dot}`}></div>
                                {cat}
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {agrupados[cat].map(art => {
                                    const formatos = getFormatosArticulo(art);
                                    const fmtElegido = formatoActivo[art.id] || art.unidad;
                                    const keyActual = `${art.id}_${fmtElegido}`;
                                    const cantActual = carrito[keyActual] ? carrito[keyActual].cantidad : 0;
                                    const sinStock = art.stock_disponible !== null && art.stock_disponible === 0;
                                    return (
                                        <div key={art.id} className={`bg-white rounded-2xl border p-4 flex flex-col gap-3 transition-shadow hover:shadow-sm ${sinStock ? 'opacity-60' : 'border-gray-100'} ${cantActual > 0 ? `${theme.borderLight} ${theme.bgLight}/40` : ''}`}>
                                            <div className="flex items-start justify-between gap-3">
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-[15px] font-bold text-gray-900 truncate">{art.nombre}</p>
                                                    <div className="flex items-center gap-3 mt-1.5 flex-wrap">
                                                        {art.stock_disponible !== null && (
                                                            <span className={`text-xs font-bold ${sinStock ? 'text-red-500' : 'text-emerald-600'}`}>
                                                                {sinStock ? 'Agotado' : `Stock: ${art.stock_disponible} ${art.unidad}`}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="flex items-center justify-between mt-auto">
                                                <div className="flex gap-1 overflow-x-auto custom-scrollbar pb-1">
                                                    {formatos.map(fmt => (
                                                        <button key={fmt} onClick={() => setFormatoActivo(p => ({ ...p, [art.id]: fmt }))}
                                                            className={`px-2 py-1 text-[11px] font-medium rounded border transition-colors whitespace-nowrap ${fmt === fmtElegido ? 'bg-gray-800 text-white border-gray-800' : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100'}`}>
                                                            {fmt}
                                                        </button>
                                                    ))}
                                                </div>
                                                <div className="flex items-center gap-1.5 shrink-0 ml-2">
                                                    {cantActual > 0 ? (
                                                        <>
                                                            <button onClick={() => setCantidad(art.id, fmtElegido, -1)}
                                                                className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors font-bold text-sm">−</button>
                                                            <input
                                                                type="number" min="1" value={cantActual}
                                                                onChange={e => setCantidadDirecta(art.id, fmtElegido, e.target.value)}
                                                                className={`w-10 text-center text-sm font-semibold ${theme.text} border-0 bg-transparent focus:outline-none`}
                                                            />
                                                            <button onClick={() => setCantidad(art.id, fmtElegido, 1)}
                                                                className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center text-gray-600 hover:bg-gray-100 transition-colors font-bold text-sm">+</button>
                                                        </>
                                                    ) : (
                                                        <button onClick={() => setCantidad(art.id, fmtElegido, 1)} disabled={sinStock}
                                                            className={`flex items-center gap-1.5 px-3 py-1.5 ${theme.bg} text-white rounded-lg text-xs font-medium hover:brightness-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed shadow-sm`}>
                                                            <Plus size={12} /> Agregar
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                        );
                    });
                })()}
                </div>

                {/* Panel lateral del carrito */}
                {totalCarrito > 0 && (
                    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
                        className="w-full lg:w-80 shrink-0 sticky top-6 z-40">
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.08)] p-5 space-y-4">
                            <div className="flex items-center justify-between pb-3 border-b border-gray-100">
                                <span className="text-[15px] font-bold text-gray-900 flex items-center gap-2">
                                    <ShoppingCart size={18} className="text-gray-700" />
                                    Tu Solicitud
                                </span>
                                <span className="bg-gray-100 text-gray-600 text-xs font-bold px-2.5 py-1 rounded-lg">
                                    {totalCarrito} item{totalCarrito !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1 custom-scrollbar">
                                {articulosEnCarrito.map(art => (
                                    <div key={art.cartKey} className="flex flex-col gap-1 p-2 bg-gray-50 rounded-xl border border-gray-100">
                                        <div className="flex items-start justify-between">
                                            <span className="text-sm font-semibold text-gray-700 leading-tight pr-2">{art.nombre}</span>
                                            <button onClick={() => { const { [art.cartKey]: _, ...r } = carrito; setCarrito(r); }}
                                                className="text-gray-400 hover:text-red-500 hover:bg-red-50 p-1 rounded-md transition-colors mt-0.5 shrink-0">
                                                <X size={14} />
                                            </button>
                                        </div>
                                        <div className="flex justify-between items-center mt-1">
                                            <span className="text-xs text-gray-500 font-medium bg-gray-200/50 px-2 py-0.5 rounded">{art.formatoElegido}</span>
                                            <span className="text-sm font-bold text-gray-700 bg-white border border-gray-200 px-2 py-0.5 rounded-md shadow-sm">Cant: {art.cantidad}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="pt-3 border-t border-gray-100">
                                <button onClick={handleEnviar} disabled={submitting}
                                    className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-sura-blue text-white rounded-xl font-bold hover:bg-sura-blue-dark transition-colors disabled:opacity-50 text-sm shadow-md hover:shadow-lg">
                                    {submitting ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                                    Confirmar Solicitud
                                </button>
                            </div>
                        </div>
                    </motion.div>
                )}
            </div>
        </motion.div>
    );

    // --- RENDER: Card de solicitud ---------------------------------------------
    const getCardStyles = (estado) => {
        switch (estado) {
            case 'pendiente':
                return { border: 'border-amber-200', bg: 'bg-amber-50/30', accent: 'bg-amber-400', iconBg: 'bg-amber-100', iconColor: 'text-amber-600' };
            case 'en_gestion':
                return { border: 'border-blue-200', bg: 'bg-blue-50/30', accent: 'bg-blue-400', iconBg: 'bg-blue-100', iconColor: 'text-blue-600' };
            case 'completada':
                return { border: 'border-emerald-200', bg: 'bg-emerald-50/30', accent: 'bg-emerald-400', iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600' };
            case 'rechazada':
                return { border: 'border-red-200', bg: 'bg-red-50/30', accent: 'bg-red-400', iconBg: 'bg-red-100', iconColor: 'text-red-600' };
            default:
                return { border: 'border-gray-200', bg: 'bg-white', accent: 'bg-gray-300', iconBg: 'bg-gray-100', iconColor: 'text-gray-600' };
        }
    };

    const renderCard = (sol, onClickFn) => {
        const styles = getCardStyles(sol.estado);
        return (
            <motion.div key={sol.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
                className={`rounded-2xl border ${styles.border} ${styles.bg} p-4 sm:p-5 hover:shadow-md transition-shadow cursor-pointer relative overflow-hidden`}
                onClick={() => onClickFn(sol)}>
                {/* Barra de acento lateral */}
                <div className={`absolute left-0 top-0 bottom-0 w-1.5 ${styles.accent}`}></div>
                
                <div className="flex items-center justify-between mb-4 pl-2">
                    <div className="flex items-center gap-3.5">
                        <div className={`p-2.5 rounded-xl ${styles.iconBg} shadow-sm`}>
                            <FileText size={18} className={styles.iconColor} />
                        </div>
                        <div>
                            <h4 className="font-bold text-gray-900 text-sm">{sol.codigo}</h4>
                            <p className="text-xs text-gray-500 mt-0.5">{new Date(sol.created_at).toLocaleDateString('es-VE')} • {new Date(sol.created_at).toLocaleTimeString('es-VE', {hour: '2-digit', minute:'2-digit'})}</p>
                        </div>
                    </div>
                    <EstadoBadge estado={sol.estado} parcial={sol.entrega_parcial} />
                </div>
                
                <div className="flex items-center gap-2.5 text-xs text-gray-600 flex-wrap pl-2">
                    {sol.empleado_nombre && (
                        <span className="flex items-center gap-1.5 bg-white border border-gray-100 shadow-sm px-2.5 py-1 rounded-md">
                            <ClipboardList size={13} className="text-gray-400" /> 
                            <span className="font-medium text-gray-700">{sol.empleado_nombre}</span>
                        </span>
                    )}
                    {sol.departamento && (
                        <span className="bg-white border border-gray-100 shadow-sm px-2.5 py-1 rounded-md text-gray-600 truncate max-w-[150px]">
                            {sol.departamento}
                        </span>
                    )}
                    {sol.sede_nombre && (
                        <span className="font-medium px-2.5 py-1 rounded-md bg-white border border-gray-100 shadow-sm text-gray-700 flex items-center gap-1.5">
                            <span className={`w-1.5 h-1.5 rounded-full ${sol.sede_nombre.includes('CDN') ? 'bg-indigo-400' : 'bg-sura-blue'}`}></span>
                            {sol.sede_nombre}
                        </span>
                    )}
                    <span className="flex items-center gap-1.5 bg-white border border-gray-100 shadow-sm px-2.5 py-1 rounded-md ml-auto">
                        <Package size={13} className="text-gray-400" /> 
                        <span className="font-semibold text-gray-700">{sol.items.length}</span> art.
                    </span>
                </div>
                
                {sol.motivo_rechazo && (
                    <div className="mt-3.5 pl-2">
                        <p className="text-xs text-red-700 bg-red-50 rounded-lg px-3 py-2 border border-red-100/50">
                            <span className="font-bold">Motivo del rechazo:</span> {sol.motivo_rechazo}
                        </p>
                    </div>
                )}
            </motion.div>
        );
    };

    // --- RENDER: Detalle de solicitud ------------------------------------------
    const renderDetalle = () => {
        if (!selectedSol) return null;
        const sol = selectedSol;
        const puedeGestionar = puedeVerBandeja && ['pendiente', 'en_gestion'].includes(sol.estado);
        return (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
                <div className="flex items-center gap-3">
                    <button onClick={() => setViewMode(puedeVerBandeja ? 'bandeja' : 'list')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                        <ArrowLeft size={20} />
                    </button>
                    <div className="flex-1">
                        <h2 className="text-lg font-bold text-gray-900">{sol.codigo}</h2>
                        <p className="text-xs text-gray-500">{new Date(sol.created_at).toLocaleDateString('es-VE', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                    <EstadoBadge estado={sol.estado} parcial={sol.entrega_parcial} />
                </div>

                {/* Info */}
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Información</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        {sol.empleado_nombre && <div><span className="text-gray-400">Solicitante</span><p className="font-medium text-gray-900">{sol.empleado_nombre}</p></div>}
                        {sol.departamento && <div><span className="text-gray-400">Departamento</span><p className="font-medium text-gray-900">{sol.departamento}</p></div>}
                        {sol.sede_nombre && <div><span className="text-gray-400">Sede</span><p className="font-medium text-sura-blue">{sol.sede_nombre}</p></div>}
                        {sol.gestionado_por && <div><span className="text-gray-400">Gestionado por</span><p className="font-medium text-gray-900">{sol.gestionado_por}</p></div>}
                        {sol.fecha_completado && <div><span className="text-gray-400">Completado</span><p className="font-medium text-gray-900">{new Date(sol.fecha_completado).toLocaleDateString('es-VE')}</p></div>}
                    </div>
                    {sol.motivo_rechazo && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-xl">
                            <p className="text-sm text-red-700"><strong>Motivo de rechazo:</strong> {sol.motivo_rechazo}</p>
                        </div>
                    )}
                    {sol.nota_recepcion && (
                        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-100 rounded-xl">
                            <p className="text-sm text-emerald-700 whitespace-pre-line"><strong>Nota de recepción:</strong> {sol.nota_recepcion}</p>
                        </div>
                    )}
                </div>

                {/* Artículos */}
                <div className="bg-white rounded-2xl border border-gray-100 p-5">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Artículos solicitados</h3>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100">
                                    <th className="text-left py-2 text-xs text-gray-400 font-medium">Artículo</th>
                                    <th className="text-left py-2 text-xs text-gray-400 font-medium">Categoría</th>
                                    <th className="text-center py-2 text-xs text-gray-400 font-medium">Cant. solicitada</th>
                                    <th className="text-center py-2 text-xs text-gray-400 font-medium">Cant. entregada</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sol.items.map(item => (
                                    <tr key={item.id} className="border-b border-gray-50 last:border-0">
                                        <td className="py-2.5 font-medium text-gray-900">{item.articulo_nombre}</td>
                                        <td className="py-2.5">
                                            <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${CATEGORIA_BADGE_CLASS}`}>
                                                {item.articulo_categoria}
                                            </span>
                                        </td>
                                        <td className="py-2.5 text-center font-semibold text-gray-700">{item.cantidad_solicitada} {item.articulo_unidad}</td>
                                        <td className="py-2.5 text-center">
                                            {item.cantidad_entregada !== null
                                                ? <span className="font-semibold text-emerald-600">{item.cantidad_entregada} {item.articulo_unidad}</span>
                                                : <span className="text-gray-300">—</span>}
                                            {sol.estado === 'pendiente' && item.cantidad_entregada > 0 && item.cantidad_pendiente > 0 && (
                                                <p className="text-xs font-semibold text-orange-600">Faltan {item.cantidad_pendiente}</p>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Acciones recepción */}
                {puedeGestionar && (
                    <div className="flex gap-3">
                        <button onClick={() => abrirCompletar(sol)}
                            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors text-sm">
                            <CheckCircle2 size={15} /> {sol.entrega_parcial ? 'Entregar lo faltante' : 'Marcar como entregada'}
                        </button>
                        {/* Con una entrega parcial ya salió material: se cierra con lo entregado, no se rechaza. */}
                        {!sol.entrega_parcial && (
                            <button onClick={() => { setSelectedSol(sol); setShowRechazarModal(true); }}
                                className="flex items-center gap-2 px-5 py-2.5 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors text-sm">
                                <XCircle size={15} /> Rechazar
                            </button>
                        )}
                    </div>
                )}

                {/* Confirmación de recepción (solicitante) */}
                {sol.estado === 'completada' && sol.empleado_id === user?.empleado_id && (
                    <div className="flex">
                        <button onClick={() => handleConfirmarRecepcion(sol.id)} disabled={submitting}
                            className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors text-sm disabled:opacity-50">
                            <CheckCircle2 size={15} /> Confirmar recepción
                        </button>
                    </div>
                )}
            </motion.div>
        );
    };

    // --- RENDER: Lista empleado ------------------------------------------------
    const renderLista = () => {
        const pendientes = solicitudes.filter(s => s.estado === 'pendiente').length;
        const completadas = solicitudes.filter(s => ['completada', 'recibido'].includes(s.estado)).length;
        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <StatCard title="Total" value={solicitudes.length} icon={ClipboardList} color="bg-sura-blue" />
                    <StatCard title="Pendientes" value={pendientes} icon={Clock} color="bg-amber-500" />
                    <StatCard title="Completadas" value={completadas} icon={CheckCircle2} color="bg-emerald-500" />
                </div>
                <div className="flex justify-between items-center">
                    <h2 className="text-base font-bold text-gray-900">Mis solicitudes</h2>
                    <button onClick={() => setViewMode('create')}
                        className="flex items-center gap-2 px-4 py-2.5 bg-sura-blue text-white rounded-xl font-medium hover:bg-sura-blue-dark transition-colors text-sm">
                        <Plus size={15} /> Nueva solicitud
                    </button>
                </div>
                {loading ? (
                    <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-gray-300" /></div>
                ) : solicitudes.length === 0 ? (
                    <div className="text-center py-16 text-gray-400">
                        <FileText size={48} className="mx-auto mb-3 opacity-25" />
                        <p className="text-sm">No tienes solicitudes aún</p>
                        <button onClick={() => setViewMode('create')} className="mt-3 text-sura-blue text-sm font-medium hover:underline">Crear tu primera solicitud</button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {solicitudes.map(sol => renderCard(sol, (s) => { setSelectedSol(s); setViewMode('detail'); }))}
                    </div>
                )}
            </motion.div>
        );
    };

    // --- RENDER: Bandeja recepción ---------------------------------------------
    const renderBandeja = () => {
        const pendientes   = solicitudes.filter(s => s.estado === 'pendiente');
        const completadas  = solicitudes.filter(s => s.estado === 'completada');
        const recibidas    = solicitudes.filter(s => s.estado === 'recibido');
        const rechazadas   = solicitudes.filter(s => s.estado === 'rechazada');
        const todas        = solicitudes;

        const filtros = [
            { key: 'pendiente',  label: 'Pendientes',  count: pendientes.length,  icon: Clock,        bg: 'bg-amber-500',   activeBg: 'bg-amber-500',   inactiveBg: 'bg-amber-500', inactiveText: 'text-white' },
            { key: 'completada', label: 'Entregadas',  count: completadas.length, icon: CheckCircle2, bg: 'bg-emerald-500', activeBg: 'bg-emerald-500', inactiveBg: 'bg-emerald-500', inactiveText: 'text-white' },
            { key: 'recibido',   label: 'Recibidas',   count: recibidas.length,   icon: CheckCircle2, bg: 'bg-cyan-500',    activeBg: 'bg-cyan-500',    inactiveBg: 'bg-cyan-500', inactiveText: 'text-white' },
            { key: 'rechazada',  label: 'Rechazadas',  count: rechazadas.length,  icon: XCircle,      bg: 'bg-red-500',    activeBg: 'bg-red-500',    inactiveBg: 'bg-red-500', inactiveText: 'text-white' },
            { key: 'todas',      label: 'Total',       count: todas.length,       icon: ClipboardList, bg: 'bg-sura-blue',  activeBg: 'bg-sura-blue',  inactiveBg: 'bg-sura-blue', inactiveText: 'text-white' },
        ];

        const mapaListas = {
            pendiente:  pendientes,
            completada: completadas,
            recibido:   recibidas,
            rechazada:  rechazadas,
            todas:      todas,
        };

        const listaActual = mapaListas[filtroBandeja] || pendientes;
        const filtroActual = filtros.find(f => f.key === filtroBandeja);

        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                {/* Tarjetas-filtro clickeables con estilo StatCard */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                    {filtros.map(f => {
                        const isActive = filtroBandeja === f.key;
                        const Icon = f.icon;
                        return (
                            <StatCard
                                key={f.key}
                                title={f.label}
                                value={f.count}
                                icon={f.icon}
                                color={f.bg}
                                onClick={() => setFiltroBandeja(f.key)}
                                active={isActive}
                            />
                        );
                    })}
                </div>

                {/* Lista filtrada */}
                {loading ? (
                    <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-gray-300" /></div>
                ) : (
                    <div className="space-y-3">
                        <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                            {filtroActual && <filtroActual.icon size={14} className={filtroActual.activeText} />}
                            {filtroActual?.label} ({listaActual.length})
                        </h2>
                        {listaActual.length > 0 ? (
                            listaActual.map(sol => renderCard(sol, (s) => { setSelectedSol(s); setViewMode('detail'); }))
                        ) : (
                            <div className="text-center py-16 text-gray-400">
                                <CheckCircle2 size={48} className="mx-auto mb-3 opacity-25" />
                                <p className="text-sm">No hay solicitudes en esta categoría</p>
                            </div>
                        )}
                    </div>
                )}
            </motion.div>
        );
    };

    // --- RENDER: Catálogo admin ------------------------------------------------
    const columns = useMemo(() => [
        {
            header: 'Artículo',
            accessorKey: 'nombre',
            cell: ({ row }) => <span className="font-medium text-gray-900">{row.original.nombre}</span>
        },
        {
            header: 'Categoría',
            accessorKey: 'categoria',
            cell: ({ row }) => (
                <span className={`whitespace-nowrap text-xs px-2 py-0.5 rounded-full border font-medium ${CATEGORIA_BADGE_CLASS}`}>
                    {row.original.categoria}
                </span>
            )
        },
        {
            header: 'Stock CDN',
            accessorKey: 'stock_cdn',
            cell: ({ row }) => {
                const v = row.original.stock_cdn, min = row.original.stock_minimo ?? 0;
                const cls = v === 0 ? 'text-red-500' : (v <= min ? 'text-amber-600' : 'text-gray-900');
                return <span className={`font-semibold flex items-center gap-1 ${cls}`}>{v}{v <= min && <AlertTriangle size={14} className="text-red-500" />}</span>;
            }
        },
        {
            header: 'Stock CA-1',
            accessorKey: 'stock_ca1',
            cell: ({ row }) => {
                const v = row.original.stock_ca1, min = row.original.stock_minimo ?? 0;
                const cls = v === 0 ? 'text-red-500' : (v <= min ? 'text-amber-600' : 'text-gray-900');
                return <span className={`font-semibold ${cls}`}>{v}{v <= min && <span className="ml-1 text-[10px] font-medium">bajo</span>}</span>;
            }
        },
        ...((puedeVerBandeja || puedeVerCatalogo) ? [{
            header: 'Acciones',
            id: 'acciones',
            cell: ({ row }) => {
                const art = row.original;
                return (
                    <div className="flex items-center gap-0.5">
                        {!art.activo && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-400 border border-gray-200 mr-1">Inactivo</span>}
                        <button onClick={() => abrirKardex(art)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-sura-blue transition-colors" title="Ver kardex">
                            <History size={13} />
                        </button>
                        <button onClick={() => {
                            setEditandoArticulo(art);
                            setFormArticulo({ nombre: art.nombre, categoria: art.categoria, unidad: art.unidad, stock_cdn: art.stock_cdn, stock_ca1: art.stock_ca1, stock_minimo: art.stock_minimo ?? 5, limite_solicitud: art.limite_solicitud ?? '', activo: art.activo, unidades_adicionales: art.unidades_adicionales || [] });
                            setShowCatalogoForm(true);
                        }} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-700 transition-colors" title="Editar artículo">
                            <Pencil size={13} />
                        </button>
                        <button onClick={() => handleToggleActivo(art)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-red-500 transition-colors" title={art.activo ? 'Desactivar' : 'Activar'}>
                            <Power size={13} />
                        </button>
                    </div>
                );
            }
        }] : [])
    ], [puedeVerBandeja, puedeVerCatalogo]);

    // -- Reportes --
    const handleExportarExcel = async () => {
        try {
            const params = new URLSearchParams();
            if (filtrosReporte.estado) params.append('estado', filtrosReporte.estado);
            if (filtrosReporte.sede_id) params.append('sede_id', filtrosReporte.sede_id);
            
            const res = await axiosInstance.get(`/api/papeleria/reporte/excel?${params.toString()}`, { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Reporte_Papeleria_${new Date().toISOString().slice(0,10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('Reporte exportado correctamente');
        } catch (err) {
            toast.error('Error al exportar reporte');
        }
    };

    const renderEstadisticas = () => {
        if (loadingStats || !statsData) {
            return <div className="flex justify-center py-20"><Loader2 size={32} className="animate-spin text-sura-blue" /></div>;
        }

        const COLORES_ESTADO = {
            pendiente: '#eab308',
            completada: '#10b981',
            recibido: '#06b6d4',
            rechazada: '#ef4444',
        };
        const LABELS_ESTADO = {
            pendiente: 'Pendientes',
            completada: 'Entregadas',
            recibido: 'Confirmadas',
            rechazada: 'Rechazadas',
        };

        // Stacked bar data para distribución por CC
        const ccData = (statsData.distribucion_deptos || []).slice(0, 10);
        const stackedData = ccData.map(dep => ({
            name: dep.departamento && dep.departamento.length > 18 ? dep.departamento.substring(0, 18) + '…' : (dep.departamento || 'Sin asignar'),
            fullName: dep.departamento || 'Sin asignar',
            CDN: dep.CDN || 0,
            CA1: dep.CA1 || 0,
        }));

        // Pie data para estado global
        const resumen = statsData.resumen || {};
        const estadoData = [
            { name: LABELS_ESTADO.pendiente, value: resumen.pendientes || 0, color: COLORES_ESTADO.pendiente },
            { name: LABELS_ESTADO.completada, value: resumen.completadas || 0, color: COLORES_ESTADO.completada },
            { name: LABELS_ESTADO.rechazada, value: resumen.rechazadas || 0, color: COLORES_ESTADO.rechazada },
        ].filter(e => e.value > 0);

        // Tendencia mensual
        const tendenciaData = (statsData.evolucion_mensual || []).map(t => ({ label: t.mes, total: t.cantidad }));

        // Top artículos
        const topArticulos = statsData.top_articulos || [];

        const kpis = [
            { label: 'Total solicitudes', value: resumen.total || 0, color: 'text-sura-blue', bg: 'bg-blue-50', border: 'border-blue-100', icon: ClipboardList },
            { label: 'Pendientes', value: resumen.pendientes || 0, color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100', icon: Clock },
            { label: 'Entregadas', value: resumen.completadas || 0, color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', icon: CheckCircle2 },
            { label: 'Rechazadas', value: resumen.rechazadas || 0, color: 'text-red-500', bg: 'bg-red-50', border: 'border-red-100', icon: XCircle },
        ];

        return (
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                    <h2 className="text-lg font-bold text-sura-blue-dark flex items-center gap-2">
                        <BarChart2 size={20} /> Dashboard de Papelería
                    </h2>
                    <select
                        value={rangoStats}
                        onChange={e => setRangoStats(e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-1.5 text-sm font-medium focus:ring-2 focus:ring-sura-blue/20 outline-none cursor-pointer bg-white"
                    >
                        <option value="todo">Todo el tiempo</option>
                        <option value="ultima_semana">Última semana</option>
                        <option value="mes_actual">Mes actual</option>
                        <option value="ultimo_mes">Últimos 30 días</option>
                        <option value="ultimos_3_meses">Últimos 3 meses</option>
                        <option value="ultimos_6_meses">Últimos 6 meses</option>
                    </select>
                </div>

                {/* KPIs */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {kpis.map(kpi => (
                        <div key={kpi.label} className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm flex flex-col justify-between">
                            <div className="flex justify-between items-start mb-2">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{kpi.label}</p>
                                <div className={`${kpi.bg} ${kpi.border} border p-1.5 rounded-md flex-shrink-0`}>
                                    <kpi.icon size={16} className={kpi.color} />
                                </div>
                            </div>
                            <p className={`text-3xl font-bold ${kpi.color}`}>{kpi.value}</p>
                        </div>
                    ))}
                </div>

                {/* Distribución por Centro de Costo (stacked bar horizontal) */}
                {stackedData.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                        <h3 className="text-sm font-bold text-sura-blue uppercase tracking-wide mb-6 flex items-center gap-2">
                            <BarChart2 size={16} /> Distribución por Centro de Costo
                        </h3>
                        <ResponsiveContainer width="100%" height={Math.max(260, stackedData.length * 42)}>
                            <BarChart data={stackedData} layout="vertical" barSize={18} margin={{ left: 10, right: 30 }} style={{ outline: 'none' }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" horizontal={false} />
                                <XAxis type="number" tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                                <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: '#374151', fontWeight: 500 }} width={160} />
                                <RechartsTooltip cursor={{ fill: '#f3f4f6' }} formatter={(v, name) => [v, name === 'CDN' ? 'Sede CDN' : 'Sede CA1']} />
                                <Legend formatter={v => <span className="text-xs font-medium text-gray-600 ml-1">{v === 'CDN' ? 'Sede CDN' : 'Sede CA1'}</span>} iconType="square" wrapperStyle={{ paddingTop: '10px' }} />
                                <Bar dataKey="CDN" stackId="a" fill="#818cf8" name="CDN" />
                                <Bar dataKey="CA1" stackId="a" fill="#0062A5" name="CA1" radius={[0, 2, 2, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Estado Global - Pie */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                        <h3 className="text-sm font-bold text-sura-blue uppercase tracking-wide mb-6">Estado Global</h3>
                        {estadoData.length === 0 ? (
                            <div className="flex items-center justify-center h-48 text-gray-400 text-sm italic">Sin datos</div>
                        ) : (
                            <ResponsiveContainer width="100%" height={240}>
                                <PieChart style={{ outline: 'none' }}>
                                    <Pie data={estadoData} dataKey="value" nameKey="name" cx="50%" cy="50%"
                                        outerRadius={90} innerRadius={55} paddingAngle={2}>
                                        {estadoData.map(entry => <Cell key={entry.name} fill={entry.color} />)}
                                    </Pie>
                                    <RechartsTooltip />
                                    <Legend formatter={v => <span className="text-xs font-medium text-gray-600 ml-1">{v}</span>} iconType="circle" />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </div>

                    {/* Tendencia Mensual - Area Chart */}
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex flex-col">
                        <h3 className="text-sm font-bold text-sura-blue uppercase tracking-wide mb-6">Tendencia Histórica (6 meses)</h3>
                        {tendenciaData.length <= 1 ? (
                            <div className="flex-1 flex items-center justify-center text-gray-400 text-sm italic min-h-[200px]">Data insuficiente</div>
                        ) : (
                            <div className="flex-1">
                                <ResponsiveContainer width="100%" height={240}>
                                    <AreaChart data={tendenciaData} style={{ outline: 'none' }}>
                                        <defs>
                                            <linearGradient id="gradBluePap" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#003366" stopOpacity={0.2} />
                                                <stop offset="95%" stopColor="#003366" stopOpacity={0} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                                        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#6b7280' }} axisLine={false} tickLine={false} />
                                        <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} axisLine={false} tickLine={false} />
                                        <RechartsTooltip formatter={v => [v, 'Solicitudes']} />
                                        <Area type="monotone" dataKey="total" stroke="#003366" strokeWidth={3} fill="url(#gradBluePap)"
                                            dot={{ fill: '#ffffff', stroke: '#003366', r: 4, strokeWidth: 2 }}
                                            activeDot={{ r: 8, fill: '#FFD100', stroke: '#003366', strokeWidth: 2 }} />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        )}
                    </div>
                </div>

                {/* Top Artículos */}
                {topArticulos.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                        <h3 className="text-sm font-bold text-sura-blue uppercase tracking-wide mb-6">Top Artículos más solicitados</h3>
                        <div className="space-y-4">
                            {topArticulos.map((art, i) => {
                                const pct = Math.round((art.cantidad / (topArticulos[0]?.cantidad || 1)) * 100);
                                return (
                                    <div key={art.nombre} className="flex items-center gap-4">
                                        <div className={`w-6 h-6 rounded flex items-center justify-center text-xs font-bold flex-shrink-0 ${i < 3 ? 'bg-sura-blue text-white' : 'bg-gray-100 text-gray-500'}`}>{i + 1}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1.5">
                                                <span className="text-sm font-medium text-gray-800 truncate">{art.nombre}</span>
                                                <span className="text-sm font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded ml-2 shrink-0">{art.cantidad}</span>
                                            </div>
                                            <div className="w-full bg-gray-100 rounded-sm h-1.5 overflow-hidden">
                                                <div className="h-full transition-all bg-sura-blue" style={{ width: `${pct}%` }} />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </motion.div>
        );
    };

    const renderReportes = () => {
        const dataReporte = solicitudes.filter(s => {
            if (filtrosReporte.estado && s.estado !== filtrosReporte.estado) return false;
            if (filtrosReporte.sede_id && String(s.sede_id) !== String(filtrosReporte.sede_id)) return false;
            return true;
        });

        return (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                <div className="flex flex-col md:flex-row gap-4 items-end bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
                    <div className="flex-1">
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Estado</label>
                        <select value={filtrosReporte.estado} onChange={e => setFiltrosReporte(p => ({ ...p, estado: e.target.value }))}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/30 cursor-pointer">
                            <option value="">Todos los estados</option>
                            <option value="pendiente">Pendiente — en espera de procura</option>
                            <option value="completada">Entregada — procura la marcó como entregada</option>
                            <option value="recibido">Recibida — confirmada por el solicitante</option>
                            <option value="rechazada">Rechazada</option>
                        </select>
                    </div>
                    <div className="flex-1">
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Sede</label>
                        <select value={filtrosReporte.sede_id} onChange={e => setFiltrosReporte(p => ({ ...p, sede_id: e.target.value }))}
                            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/30 cursor-pointer">
                            <option value="">Todas las sedes</option>
                            <option value="1">CDN</option>
                            <option value="2">CA1</option>
                        </select>
                    </div>
                    <div className="shrink-0 w-full md:w-auto">
                        <button onClick={handleExportarExcel} className="w-full md:w-auto flex items-center justify-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-xl text-sm font-bold hover:bg-emerald-700 transition-colors shadow-sm">
                            <ClipboardList size={16} /> Exportar Excel
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mt-4">
                    <TableView
                        data={dataReporte}
                        columns={[
                            { accessorKey: 'codigo', header: 'Código', cell: ({ row }) => <span className="font-bold text-sura-blue text-xs cursor-pointer" onClick={() => { setSelectedSol(row.original); setViewMode('detail'); }}>{row.original.codigo}</span> },
                            { accessorKey: 'created_at', header: 'Fecha', cell: ({ row }) => new Date(row.original.created_at).toLocaleDateString('es-VE', { day:'2-digit', month:'2-digit', year:'2-digit' }) },
                            { accessorKey: 'empleado_nombre', header: 'Solicitante' },
                            { accessorKey: 'departamento', header: 'Centro de Costos' },
                            { accessorKey: 'sede_nombre', header: 'Sede' },
                            { accessorKey: 'estado', header: 'Estado', cell: ({ row }) => <EstadoBadge estado={row.original.estado} /> },
                        ]}
                        defaultSort={{ key: 'created_at', direction: 'desc' }}
                        searchable={true}
                        searchKeys={['codigo', 'empleado_nombre', 'departamento']}
                        itemsPerPage={10}
                    />
                </div>
            </motion.div>
        );
    };

    const renderCatalogo = () => (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5">
            <div className="flex items-center gap-3">
                <button onClick={() => setViewMode('list')} className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
                    <ArrowLeft size={20} />
                </button>
                <h2 className="text-lg font-bold text-gray-900 flex-1">Catálogo y Visualización de Stock</h2>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden mt-4">
                <TableView
                    data={articulosCatalogo}
                    columns={columns}
                    loading={loading}
                    error={null}
                    columnVisibility={columnVisibility}
                    setColumnVisibility={setColumnVisibility}
                    defaultPageSize={20}
                    defaultSorting={[{ id: 'nombre', desc: false }]}
                    activeActionMenu={activeActionMenu}
                    setActiveActionMenu={setActiveActionMenu}
                    hideColumnSettings={true}
                    availableFilters={[
                        { id: 'categoria', placeholder: 'Todas las categorías' }
                    ]}
                    onFilteredCountChange={() => {}}
                />
            </div>
        </motion.div>
    );

    const renderReponerStock = () => {
        const catsReponer = ['Todas', ...Array.from(new Set(articulosCatalogo.map(a => a.categoria)))];
        const filtrados = articulosCatalogo.filter(a => {
            const matchSearch = a.nombre.toLowerCase().includes(reponerSearch.toLowerCase()) || a.categoria.toLowerCase().includes(reponerSearch.toLowerCase());
            const matchCat = reponerCategoria === 'Todas' || a.categoria === reponerCategoria;
            return matchSearch && matchCat;
        });

        return (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <PackagePlus className="text-sura-blue" size={22} />
                            Ingresar Stock
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5">Registra el ingreso en el formato disponible; las unidades se calculan automáticamente.</p>
                    </div>
                    <button
                        onClick={() => { setEditandoArticulo(null); setFormArticulo({ nombre: '', categoria: 'ARTÍCULOS DE OFICINA', unidad: 'Unidad', stock_cdn: 0, stock_ca1: 0, stock_minimo: 5, limite_solicitud: '', activo: true, unidades_adicionales: [] }); setShowCatalogoForm(true); }}
                        className="flex items-center justify-center gap-2 px-5 py-2.5 bg-sura-blue text-white rounded-xl text-sm font-semibold hover:bg-sura-blue-dark transition-colors shadow-md whitespace-nowrap"
                    >
                        <Plus size={16} /> Nuevo Artículo
                    </button>
                </div>

                {/* Buscador y filtro */}
                <div className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search size={17} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                        <input
                            type="text"
                            placeholder="Buscar producto..."
                            value={reponerSearch}
                            onChange={e => setReponerSearch(e.target.value)}
                            className="block w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-sura-blue focus:border-sura-blue shadow-sm transition-all"
                        />
                    </div>
                    <select
                        value={reponerCategoria}
                        onChange={e => setReponerCategoria(e.target.value)}
                        className="md:w-56 px-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-sura-blue shadow-sm cursor-pointer appearance-none"
                    >
                        {catsReponer.map(cat => <option key={cat} value={cat}>{cat}</option>)}
                    </select>
                </div>

                {/* Grid de productos */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtrados.length === 0 ? (
                        <div className="col-span-full py-16 text-center bg-white rounded-2xl border border-gray-100">
                            <PackageSearch size={40} className="mx-auto text-gray-300 mb-3" />
                            <p className="text-gray-500 font-medium">No se encontraron artículos</p>
                        </div>
                    ) : (
                        filtrados.slice(0, 50).map(art => {
                            const inputs = reponerInputs[art.id] || { ca1: 0, cdn: 0 };
                            const eq = art.unidades_adicionales && art.unidades_adicionales.length > 0 ? art.unidades_adicionales[0] : null;
                            // Empaque principal: solo para mostrar el stock actual en cajas
                            const empaqueNombre = eq ? eq.nombre : 'Caja';
                            const equivStock = eq ? eq.equivalencia : 1;
                            // Formato con el que se va a ingresar (se puede elegir si hay varios)
                            const formatos = formatosIngreso(art);
                            const formato = formatos[Math.min(reponerFormato[art.id] ?? 0, formatos.length - 1)];
                            const equivalencia = formato.equivalencia;
                            const unidadesCDN = (inputs.cdn || 0) * equivalencia;
                            const unidadesCA1 = (inputs.ca1 || 0) * equivalencia;
                            const hayIngreso = inputs.cdn > 0 || inputs.ca1 > 0;

                            return (
                                <div key={art.id} className={`bg-white rounded-2xl border shadow-sm transition-all flex flex-col ${hayIngreso ? 'border-sura-blue/40 shadow-sura-blue/10' : 'border-gray-200 hover:border-gray-300 hover:shadow-md'}`}>
                                    {/* Cabecera del producto */}
                                    <div className="p-4 pb-3 border-b border-gray-100">
                                        <div className="flex items-start justify-between gap-2 mb-3 min-h-[2.5rem]">
                                            <h3 className="font-bold text-gray-800 text-sm leading-tight">{art.nombre}</h3>
                                            <span className="shrink-0 text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full border border-gray-200">
                                                {art.categoria.split('/')[0].trim()}
                                            </span>
                                        </div>

                                        {/* Stock actual */}
                                        <div className="flex gap-3 text-xs">
                                            <div className="flex-1 min-h-[86px] bg-blue-50 rounded-lg px-3 py-2 border border-blue-100">
                                                <div className="text-sura-blue font-medium mb-0.5">CDN</div>
                                                <div className="font-bold text-gray-800 text-base">{art.stock_cdn} <span className="text-[10px] font-normal text-gray-500">{pluralEmpaque(art.unidad)}</span></div>
                                                {equivStock > 1 && <div className="text-[10px] text-gray-500">{Math.floor(art.stock_cdn / equivStock)} {pluralEmpaque(empaqueNombre)}</div>}
                                            </div>
                                            <div className="flex-1 min-h-[86px] bg-amber-50 rounded-lg px-3 py-2 border border-amber-100">
                                                <div className="text-amber-700 font-medium mb-0.5">CA1</div>
                                                <div className="font-bold text-gray-800 text-base">{art.stock_ca1} <span className="text-[10px] font-normal text-gray-500">{pluralEmpaque(art.unidad)}</span></div>
                                                {equivStock > 1 && <div className="text-[10px] text-amber-700">{Math.floor(art.stock_ca1 / equivStock)} {pluralEmpaque(empaqueNombre)}</div>}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Sección de ingreso */}
                                    <div className="p-4 space-y-3 flex-1 flex flex-col">
                                        {/* Área de formato fija: todos los productos conservan la misma retícula. */}
                                        <div className="space-y-1.5 min-h-[68px]">
                                            <p className="flex items-center gap-1.5 text-[11px] font-semibold text-gray-500">
                                                <Package size={12} className="text-sura-blue" /> Ingresar en
                                            </p>
                                            {formatos.length === 1 ? (
                                                <div className="h-12 w-full flex items-center rounded-xl border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700">
                                                    {pluralEmpaque(formato.nombre)}
                                                    {formato.equivalencia > 1 && <span className="ml-1.5 text-gray-500">· 1 = {formato.equivalencia} {pluralEmpaque(art.unidad)}</span>}
                                                </div>
                                            ) : (
                                                <SelectorFormato
                                                    formatos={formatos}
                                                    value={formatos.indexOf(formato)}
                                                    onChange={i => setReponerFormato(prev => ({ ...prev, [art.id]: i }))}
                                                    unidadBase={art.unidad}
                                                    label={`Formato de ingreso para ${art.nombre}`}
                                                />
                                            )}
                                        </div>

                                        {/* Inputs CDN y CA1 */}
                                        <div className="space-y-2.5">
                                            {/* CDN */}
                                            <div className="flex items-center gap-2">
                                                <div className="w-10 shrink-0 text-center">
                                                    <span className="text-[10px] font-bold text-sura-blue bg-sura-blue/5 border border-sura-blue/20 rounded px-1.5 py-0.5">CDN</span>
                                                </div>
                                                <div className="flex-1 flex items-center rounded-xl border border-gray-200 bg-white overflow-hidden">
                                                    <button
                                                        onClick={() => updateReponerInput(art.id, 'cdn', -1)}
                                                        className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-gray-700 text-lg font-bold transition-colors shrink-0"
                                                    >−</button>
                                                    <input
                                                        type="number" min="0"
                                                        value={inputs.cdn || ''}
                                                        placeholder="0"
                                                        onChange={e => {
                                                            const val = parseInt(e.target.value) || 0;
                                                            setReponerInputs(prev => ({ ...prev, [art.id]: { ca1: 0, cdn: 0, ...prev[art.id], cdn: Math.max(0, val) } }));
                                                        }}
                                                        className="flex-1 text-center py-2 text-sm font-bold bg-transparent border-none focus:ring-0 p-0 min-w-0"
                                                    />
                                                    <button
                                                        onClick={() => updateReponerInput(art.id, 'cdn', 1)}
                                                        className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-gray-700 text-lg font-bold transition-colors shrink-0"
                                                    >+</button>
                                                </div>
                                                {inputs.cdn > 0 && equivalencia > 1 && (
                                                    <div className="shrink-0 text-right">
                                                        <span className="text-[10px] text-sura-blue font-semibold">+{unidadesCDN} und.</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* CA1 */}
                                            <div className="flex items-center gap-2">
                                                <div className="w-10 shrink-0 text-center">
                                                    <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">CA1</span>
                                                </div>
                                                <div className="flex-1 flex items-center rounded-xl border border-gray-200 bg-white overflow-hidden">
                                                    <button
                                                        onClick={() => updateReponerInput(art.id, 'ca1', -1)}
                                                        className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-gray-700 text-lg font-bold transition-colors shrink-0"
                                                    >−</button>
                                                    <input
                                                        type="number" min="0"
                                                        value={inputs.ca1 || ''}
                                                        placeholder="0"
                                                        onChange={e => {
                                                            const val = parseInt(e.target.value) || 0;
                                                            setReponerInputs(prev => ({ ...prev, [art.id]: { ca1: 0, cdn: 0, ...prev[art.id], ca1: Math.max(0, val) } }));
                                                        }}
                                                        className="flex-1 text-center py-2 text-sm font-bold bg-transparent border-none focus:ring-0 p-0 min-w-0"
                                                    />
                                                    <button
                                                        onClick={() => updateReponerInput(art.id, 'ca1', 1)}
                                                        className="w-9 h-9 flex items-center justify-center text-gray-500 hover:bg-gray-200 hover:text-gray-700 text-lg font-bold transition-colors shrink-0"
                                                    >+</button>
                                                </div>
                                                {inputs.ca1 > 0 && equivalencia > 1 && (
                                                    <div className="shrink-0 text-right">
                                                        <span className="text-[10px] text-sura-blue font-semibold">+{unidadesCA1} und.</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Botón guardar */}
                                        <button
                                            onClick={() => handleGuardarIngresoStock(art, formato)}
                                            disabled={submitting || !hayIngreso}
                                            className="mt-auto w-full py-2.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-100 disabled:cursor-not-allowed disabled:bg-blue-100 disabled:text-sura-blue/50 disabled:shadow-none bg-sura-blue hover:bg-sura-blue-dark text-white shadow-sm shadow-sura-blue/20"
                                        >
                                            {submitting ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                                            Confirmar Ingreso
                                        </button>
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </motion.div>
        );
    };


    // --- RENDER PRINCIPAL ------------------------------------------------------
    return (
        <div className="max-w-5xl mx-auto">
            {errorCarga && (
                <div className="mb-4 flex items-start gap-2 bg-red-50 border border-red-100 text-red-700 px-4 py-3 rounded-xl text-sm">
                    <AlertCircle size={16} className="mt-0.5 shrink-0" />
                    <span>{errorCarga}</span>
                </div>
            )}
            {/* Tabs de navegación por rol */}
            {(puedeVerBandeja || puedeVerCatalogo || puedeVerEstadisticas || puedeVerReportes) && !['create', 'detail'].includes(viewMode) && (
                <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
                    {puedeVerBandeja && (
                        <button onClick={() => setViewMode('bandeja')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'bandeja' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                            Bandeja
                        </button>
                    )}
                    {puedeVerCatalogo && (
                        <button onClick={() => setViewMode('catalogo')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'catalogo' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                            Catálogo / Stock
                        </button>
                    )}
                    {puedeVerReponerStock && (
                        <button onClick={() => setViewMode('reponer_stock')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-1 ${viewMode === 'reponer_stock' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                            <PackagePlus size={16} /> Ingresar Stock
                        </button>
                    )}
                    {puedeVerEstadisticas && (
                        <button onClick={() => setViewMode('estadisticas')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'estadisticas' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                            Estadísticas
                        </button>
                    )}
                    {puedeVerReportes && (
                        <button onClick={() => setViewMode('reportes')}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'reportes' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                            Reportes
                        </button>
                    )}
                    <button onClick={() => setViewMode('list')}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${viewMode === 'list' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
                        Mis solicitudes
                    </button>
                </div>
            )}

            <AnimatePresence mode="wait">
                {viewMode === 'list' && renderLista()}
                {viewMode === 'create' && renderCrear()}
                {viewMode === 'detail' && renderDetalle()}
                {viewMode === 'bandeja' && renderBandeja()}
                {viewMode === 'catalogo' && renderCatalogo()}
                {viewMode === 'reponer_stock' && renderReponerStock()}
                {viewMode === 'estadisticas' && renderEstadisticas()}
                {viewMode === 'reportes' && renderReportes()}
            </AnimatePresence>

            {/* Modal: Completar entrega */}
            {showCompletarModal && selectedSol && createPortal(
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowCompletarModal(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-lg space-y-4" onClick={e => e.stopPropagation()}>
                        <h3 className="text-base font-bold text-gray-900">Registrar entrega</h3>
                        <p className="text-xs text-gray-500">Indica lo que entregas ahora. Si entregas menos de lo pedido, la solicitud queda pendiente por lo faltante. El stock se descontará automáticamente.</p>
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                            {selectedSol.items.map(item => {
                                const pendiente = item.cantidad_pendiente ?? item.cantidad_solicitada;
                                const yaEntregado = item.cantidad_entregada || 0;
                                return (
                                    <div key={item.id} className="flex items-center justify-between gap-3 py-2 border-b border-gray-50 last:border-0">
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">{item.articulo_nombre}</p>
                                            <p className="text-xs text-gray-400">
                                                Solicitado: {item.cantidad_solicitada} {item.articulo_unidad}
                                                {yaEntregado > 0 && ` · Ya entregado: ${yaEntregado}`}
                                            </p>
                                        </div>
                                        {pendiente === 0 ? (
                                            <span className="text-xs font-semibold text-emerald-600 shrink-0">Entregado completo</span>
                                        ) : (
                                            <div className="flex items-center gap-2 shrink-0">
                                                <label className="text-xs text-gray-500">Entregado:</label>
                                                <input type="number" min="0" max={pendiente}
                                                    value={entregas[item.id] ?? pendiente}
                                                    onChange={e => setEntregas(prev => ({ ...prev, [item.id]: Math.min(Math.max(parseInt(e.target.value) || 0, 0), pendiente) }))}
                                                    className="w-16 px-2 py-1 border border-gray-200 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-emerald-100" />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                        {(() => {
                            const faltantes = selectedSol.items
                                .map(i => ({ i, falta: (i.cantidad_pendiente ?? i.cantidad_solicitada) - (entregas[i.id] ?? (i.cantidad_pendiente ?? i.cantidad_solicitada)) }))
                                .filter(x => x.falta > 0);
                            if (faltantes.length === 0) return null;
                            return (
                                <div className="p-3 bg-orange-50 border border-orange-100 rounded-xl text-xs text-orange-700">
                                    <p className="font-semibold mb-1">{cerrarConLoEntregado ? 'No se entregará:' : 'Quedará pendiente por entregar:'}</p>
                                    <p>{faltantes.map(x => `${x.i.articulo_nombre} (${x.falta})`).join(', ')}</p>
                                    <p className="mt-1">
                                        {cerrarConLoEntregado
                                            ? 'La solicitud se cierra como entregada y se le avisa al solicitante lo que no va a recibir.'
                                            : 'La solicitud seguirá en Pendiente y se anotará lo que falta.'}
                                    </p>
                                    <label className="flex items-start gap-2 mt-2 pt-2 border-t border-orange-100 cursor-pointer">
                                        <input type="checkbox" checked={cerrarConLoEntregado}
                                            onChange={e => setCerrarConLoEntregado(e.target.checked)}
                                            className="mt-0.5 rounded border-orange-300 text-orange-600 focus:ring-orange-200" />
                                        <span>Lo que falta ya no se va a entregar: cerrar la solicitud con lo entregado</span>
                                    </label>
                                </div>
                            );
                        })()}
                        <textarea value={notaRecepcion} onChange={e => setNotaRecepcion(e.target.value)}
                            placeholder="Nota de recepción (opcional)..." rows={2}
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 resize-none" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => setShowCompletarModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                            <button onClick={handleCompletar} disabled={submitting}
                                className="px-4 py-2 text-sm bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
                                {submitting ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Confirmar entrega
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal: Rechazar */}
            {showRechazarModal && createPortal(
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowRechazarModal(false)}>
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md space-y-4" onClick={e => e.stopPropagation()}>
                        <h3 className="text-base font-bold text-gray-900">Rechazar solicitud</h3>
                        <textarea value={motivoRechazo} onChange={e => setMotivoRechazo(e.target.value)}
                            placeholder="Motivo del rechazo..." rows={3} autoFocus
                            className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-red-100 resize-none" />
                        <div className="flex justify-end gap-2">
                            <button onClick={() => { setShowRechazarModal(false); setMotivoRechazo(''); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                            <button onClick={handleRechazar} disabled={submitting || !motivoRechazo.trim()}
                                className="px-4 py-2 text-sm bg-red-600 text-white rounded-xl hover:bg-red-700 disabled:opacity-50">
                                {submitting ? 'Rechazando...' : 'Rechazar'}
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Modal: Formulario de Catálogo */}
            {showCatalogoForm && createPortal(
                <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-3 sm:p-4" onClick={() => setShowCatalogoForm(false)}>
                    <motion.div initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} transition={{ duration: 0.18 }}
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-xl flex flex-col max-h-[92vh]" onClick={e => e.stopPropagation()}>

                        {/* Header fijo */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
                            <div className="flex items-center gap-3">
                                <div className="w-8 h-8 bg-sura-blue/10 rounded-xl flex items-center justify-center">
                                    <Package size={16} className="text-sura-blue" />
                                </div>
                                <h3 className="text-base font-bold text-gray-900">{editandoArticulo ? 'Editar artículo' : 'Nuevo artículo'}</h3>
                            </div>
                            <button onClick={() => setShowCatalogoForm(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-xl transition-colors">
                                <X size={18} />
                            </button>
                        </div>

                        {/* Cuerpo scrolleable */}
                        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

                            {/* Nombre */}
                            <div>
                                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nombre del artículo <span className="text-red-400">*</span></label>
                                <input type="text" value={formArticulo.nombre}
                                    onChange={e => setFormArticulo(p => ({ ...p, nombre: e.target.value }))}
                                    placeholder="Ej. Bolígrafos azules..."
                                    className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/30 focus:bg-white transition-all" />
                            </div>

                            {/* Categoría + Formato base */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Categoría</label>
                                    <select value={formArticulo.categoria}
                                        onChange={e => setFormArticulo(p => ({ ...p, categoria: e.target.value }))}
                                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/30 focus:bg-white transition-all cursor-pointer">
                                        <option>PAPELERÍA/CARPETAS</option>
                                        <option>ARTÍCULOS DE OFICINA</option>
                                        <option>OTROS ESPECÍFICOS</option>
                                        <option>General</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Unidad base</label>
                                    <select value={formArticulo.unidad}
                                        onChange={e => setFormArticulo(p => ({ ...p, unidad: e.target.value }))}
                                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/30 focus:bg-white transition-all cursor-pointer">
                                        <option>Unidad</option>
                                        <option>Caja</option>
                                        <option>Paquete</option>
                                        <option>Resmas</option>
                                        <option>Folios</option>
                                    </select>
                                </div>
                            </div>

                            {/* Empaque / Formatos adicionales */}
                            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <div>
                                        <p className="text-xs font-bold text-gray-700">Empaque de ingreso</p>
                                        <p className="text-[11px] text-gray-400 mt-0.5">Cómo Procura ingresa el stock (ej: Caja de 12 unidades)</p>
                                    </div>
                                    <button type="button"
                                        onClick={() => setFormArticulo(p => ({ ...p, unidades_adicionales: [...(p.unidades_adicionales || []), { nombre: 'Caja', equivalencia: 12 }] }))}
                                        className="flex items-center gap-1 text-[11px] font-bold text-sura-blue hover:text-sura-blue-dark bg-white border border-sura-blue/20 hover:border-sura-blue/40 px-2.5 py-1.5 rounded-lg transition-colors whitespace-nowrap">
                                        <Plus size={12} /> Agregar
                                    </button>
                                </div>
                                <div className="space-y-2">
                                    {(formArticulo.unidades_adicionales || []).map((ua, idx) => (
                                        <div key={idx} className="flex gap-2 items-center bg-white p-2.5 rounded-xl border border-gray-200">
                                            <div className="flex-1 flex items-center gap-2">
                                                <input type="text" value={ua.nombre} placeholder="Nombre (ej. Caja)"
                                                    onChange={e => {
                                                        const arr = [...formArticulo.unidades_adicionales];
                                                        arr[idx].nombre = e.target.value;
                                                        setFormArticulo(p => ({ ...p, unidades_adicionales: arr }));
                                                    }}
                                                    className="flex-1 min-w-0 px-3 py-1.5 text-sm rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-sura-blue/50 focus:bg-white" />
                                                <span className="text-[11px] text-gray-400 shrink-0">= </span>
                                                <input type="number" min="1" value={ua.equivalencia} placeholder="N"
                                                    onChange={e => {
                                                        const arr = [...formArticulo.unidades_adicionales];
                                                        arr[idx].equivalencia = parseInt(e.target.value) || 1;
                                                        setFormArticulo(p => ({ ...p, unidades_adicionales: arr }));
                                                    }}
                                                    className="w-14 px-2 py-1.5 text-sm text-center rounded-lg border border-gray-200 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-sura-blue/50 focus:bg-white" />
                                                <span className="text-[11px] text-gray-500 shrink-0 font-medium">{formArticulo.unidad}s</span>
                                            </div>
                                            <button type="button"
                                                onClick={() => {
                                                    const arr = formArticulo.unidades_adicionales.filter((_, i) => i !== idx);
                                                    setFormArticulo(p => ({ ...p, unidades_adicionales: arr }));
                                                }}
                                                className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors shrink-0">
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                    {(!formArticulo.unidades_adicionales || formArticulo.unidades_adicionales.length === 0) && (
                                        <div className="flex items-center gap-2 py-2 px-3 bg-white rounded-xl border border-dashed border-gray-200">
                                            <Package size={14} className="text-gray-300" />
                                            <p className="text-[11px] text-gray-400">Sin empaque configurado — se ingresará directamente en unidades base</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Stock CDN + CA1 */}
                            <div>
                                <p className="text-xs font-bold text-gray-600 mb-2">Stock actual</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="bg-blue-50 border border-blue-100 rounded-xl p-3">
                                        <label className="block text-[11px] font-semibold text-blue-600 mb-1.5">CDN — Sede 1</label>
                                        <input type="number" min="0" value={formArticulo.stock_cdn}
                                            onChange={e => setFormArticulo(p => ({ ...p, stock_cdn: parseInt(e.target.value) || 0 }))}
                                            className="w-full px-3 py-2 bg-white border border-blue-200 rounded-lg text-sm font-bold text-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-300 transition-all text-center" />
                                    </div>
                                    <div className="bg-orange-50 border border-orange-100 rounded-xl p-3">
                                        <label className="block text-[11px] font-semibold text-orange-600 mb-1.5">CA-1 — Sede 2</label>
                                        <input type="number" min="0" value={formArticulo.stock_ca1}
                                            onChange={e => setFormArticulo(p => ({ ...p, stock_ca1: parseInt(e.target.value) || 0 }))}
                                            className="w-full px-3 py-2 bg-white border border-orange-200 rounded-lg text-sm font-bold text-orange-900 focus:outline-none focus:ring-2 focus:ring-orange-300 transition-all text-center" />
                                    </div>
                                </div>
                            </div>
                            
                            {/* Stock mínimo + Límite */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Stock mínimo</label>
                                    <input type="number" min="0" value={formArticulo.stock_minimo}
                                        onChange={e => setFormArticulo(p => ({ ...p, stock_minimo: parseInt(e.target.value) || 0 }))}
                                        className="w-full px-3.5 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/30 focus:bg-white transition-all" />
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">Límite solicitud</label>
                                    <input type="number" min="0" value={formArticulo.limite_solicitud}
                                        onChange={e => setFormArticulo(p => ({ ...p, limite_solicitud: e.target.value }))}
                                        placeholder="Sin límite"
                                        className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sura-blue/30 focus:bg-white transition-all" />
                                </div>
                            </div>

                            {editandoArticulo && (
                                <label className="sm:col-span-2 flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={formArticulo.activo} onChange={e => setFormArticulo(p => ({ ...p, activo: e.target.checked }))}
                                        className="w-4 h-4 rounded border-gray-300 text-sura-blue focus:ring-sura-blue/30" />
                                    <span className="text-sm text-gray-700">Artículo activo (visible para los empleados)</span>
                                </label>
                            )}
                            {editandoArticulo && (
                                <p className="text-[11px] text-gray-400 mt-3">Editar el stock aquí queda registrado como un ajuste en el kardex. Para entradas de inventario usa el botón “Reponer”.</p>
                            )}
                        </div>

                        {/* Footer fijo con botones */}
                        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50/50 rounded-b-2xl shrink-0">
                            <button onClick={() => setShowCatalogoForm(false)}
                                className="px-4 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 rounded-xl transition-colors">
                                Cancelar
                            </button>
                            <button onClick={handleGuardarArticulo} disabled={submitting}
                                className="px-5 py-2.5 text-sm bg-sura-blue text-white rounded-xl font-bold hover:bg-sura-blue-dark disabled:opacity-50 flex items-center gap-2 shadow-md hover:shadow-lg transition-all">
                                {submitting ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
                                {editandoArticulo ? 'Guardar cambios' : 'Crear artículo'}
                            </button>
                        </div>
                    </motion.div>
                </div>,
                document.body
            )}
            {/* Modal: Kardex (historial de movimientos) */}
            {showKardexModal && articuloKardex && createPortal(
                <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowKardexModal(false)}>
                    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
                        className="bg-white rounded-3xl p-6 max-w-2xl w-full shadow-2xl relative max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-2">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900">Kardex</h3>
                                <p className="text-sm text-gray-500">{articuloKardex.nombre}</p>
                            </div>
                            <button onClick={() => setShowKardexModal(false)} className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-1.5 rounded-xl transition-colors"><X size={18} /></button>
                        </div>
                        <div className="flex gap-4 text-xs text-gray-500 mb-3">
                            <span>CDN: <strong className="text-gray-900">{articuloKardex.stock_cdn}</strong></span>
                            <span>CA-1: <strong className="text-gray-900">{articuloKardex.stock_ca1}</strong></span>
                            <span>Mínimo: <strong className="text-gray-900">{articuloKardex.stock_minimo}</strong></span>
                        </div>
                        <div className="overflow-y-auto flex-1 -mx-2 px-2">
                            {loadingMov ? (
                                <div className="flex justify-center py-10"><Loader2 size={22} className="animate-spin text-gray-400" /></div>
                            ) : movimientos.length === 0 ? (
                                <p className="text-sm text-gray-400 text-center py-10">Sin movimientos registrados</p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-100 text-xs text-gray-400">
                                            <th className="text-left py-2 font-medium">Fecha</th>
                                            <th className="text-left py-2 font-medium">Tipo</th>
                                            <th className="text-center py-2 font-medium">Alm.</th>
                                            <th className="text-right py-2 font-medium">Cant.</th>
                                            <th className="text-right py-2 font-medium">Stock</th>
                                            <th className="text-left py-2 font-medium pl-3">Motivo</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {movimientos.map(m => {
                                            const tcls = m.tipo === 'entrada' ? 'bg-emerald-50 text-emerald-600' : m.tipo === 'salida' ? 'bg-red-50 text-red-500' : 'bg-amber-50 text-amber-600';
                                            return (
                                                <tr key={m.id} className="border-b border-gray-50 last:border-0">
                                                    <td className="py-2 text-gray-500 whitespace-nowrap">{new Date(m.created_at).toLocaleDateString('es-VE', { day: '2-digit', month: 'short', year: '2-digit' })}</td>
                                                    <td className="py-2"><span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${tcls}`}>{m.tipo}</span></td>
                                                    <td className="py-2 text-center text-gray-500">{m.almacen}</td>
                                                    <td className={`py-2 text-right font-semibold ${m.cantidad >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>{m.cantidad > 0 ? '+' : ''}{m.cantidad}</td>
                                                    <td className="py-2 text-right text-gray-700">{m.stock_resultante ?? '—'}</td>
                                                    <td className="py-2 pl-3 text-gray-500">{m.motivo}{m.usuario_nombre ? <span className="text-gray-300"> · {m.usuario_nombre}</span> : ''}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </motion.div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default SolicitudPapeleriaView;
